import React, { useState, useEffect } from "react";
import SEO from "../seo";
const DiscountBundle = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "how-to-access",
        "set-up-your-campaign",
        "preview-and-publish",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Discount Bundle | xPage Help Center"
        description="Learn how to create and manage Discount Bundles in xPage. Offer multiple products together at a discounted price to increase average order value and sales performance."
        keywords="xPage, discount bundle, product bundle, create offers, sales campaign, bundle discount, promotions, eCommerce marketing, increase order value"
        canonical="https://help.xpage.ai/help/business-expansion/Discount%20Bundle"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Discount Bundle
          </h1>
          <p className="text-sm text-systemLabel">
            A Discount Bundle allows you to offer a set of products together at
            a discounted price, encouraging customers to purchase more items at
            once. This strategy increases the average order value and provides
            an incentive for shoppers to explore complementary products.
          </p>

          <h3 className="text-sm font-semibold text-systemTitle mb-2 mt-4">
            Example:
          </h3>
          <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
            <p className="text-sm text-systemLabel">
              Buy 3 T-shirts together and get 20% off the total bundle.
            </p>
          </div>
        </header>

        {/* How to Access Section */}
        <section id="how-to-access" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to Access
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">Log in to your Xpage Admin.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the left sidebar, go to Offers → Create Bundle.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Select Discount Bundle.</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Set Up Your Campaign Section */}
        <section id="set-up-your-campaign" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Set Up Your Campaign
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Setting
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Discount Method
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        Automatic discount: Applied automatically on the product
                        page, cart, and checkout.
                      </li>
                      <li>
                        Discount code: Customers must enter a code at checkout.
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Discount Title
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Choose a clear, descriptive name (e.g., Summer T-Shirt
                    Bundle 20% Off).
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Discount Bundle Setup
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Select the products to include in the bundle.</li>
                      <li>
                        Define the type of discount:
                        <ul className="list-circle list-inside space-y-1 ml-4 mt-1">
                          <li>Percentage off the total bundle</li>
                          <li>Fixed amount off the total bundle</li>
                        </ul>
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Preview and Publish Section */}
        <section id="preview-and-publish" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Preview and Publish
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  Review all settings in the Overview section.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Preview (top-right) to check your bundle setup.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Save to finalize and activate the discount bundle.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  After saving, the bundle will appear in your Offers list,
                  where you can edit, activate, deactivate, or delete it
                  anytime.
                </span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                Discount Bundles let you craft promotions that motivate
                customers to buy multiple products at once, driving higher sales
                while maintaining flexibility and control.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#how-to-access"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "how-to-access"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                How to Access
              </a>
            </li>
            <li>
              <a
                href="#set-up-your-campaign"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "set-up-your-campaign"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Set Up Your Campaign
              </a>
            </li>
            <li>
              <a
                href="#preview-and-publish"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "preview-and-publish"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Preview and Publish
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DiscountBundle;
